import { Fragment, useEffect } from 'react';
import { Link, Outlet, useOutletContext, useParams } from 'react-router-dom';
import { EnvironmentTag, ErrorAlert, Spinner, useApi } from '@pryme/ui';
import { useDev } from '../lib/context';
import { ROLE_LABEL, STATUS_LABEL, statusTone, type Service } from '../lib/types';

export interface ServiceOutlet {
  service: Service;
  reload(): Promise<void>;
}

export const useServiceOutlet = () => useOutletContext<ServiceOutlet>();

/** The service's trust environment (always the deployment's). */
export function ServiceEnvBadge(props: { environment: string }) {
  return <EnvironmentTag environment={props.environment} />;
}

export function ServiceLayout() {
  const { serviceId } = useParams();
  const { reloadServices } = useDev();
  const { data, error, loading, reload } = useApi<Service>(serviceId ? `/v1/services/${encodeURIComponent(serviceId)}` : null);

  // Keep the dashboard list in sync after mutations on this service.
  useEffect(() => {
    if (data) void reloadServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.status, data?.keys.length, data?.redirectUris.length]);

  if (!data || data.id !== serviceId) {
    return loading || (data && !error) ? <Spinner label="Loading service…" /> : (
      <>
        <ErrorAlert error={error} />
        <Link to="/">Back to dashboard</Link>
      </>
    );
  }
  return (
    <>
      <header className="p-page-head">
        <div>
          <span className="p-eyebrow">Service</span>
          <h1>{data.displayName}</h1>
          <div className="dv-service-meta">
            <ServiceEnvBadge environment={data.environment} />
            <span className={`p-status ${statusTone(data.status)}`}>{STATUS_LABEL[data.status]}</span>
            {data.role && (
              <>
                <span className="dv-meta-sep" aria-hidden="true" />
                <span className="p-small p-muted">Your role: {ROLE_LABEL[data.role]}</span>
              </>
            )}
            <span className="dv-meta-sep" aria-hidden="true" />
            <code className="p-mono-chip">{data.id}</code>
          </div>
        </div>
      </header>
      {/* Keyed so per-page state (including pasted secrets) never carries over to another service. */}
      <Fragment key={data.id}>
        <Outlet context={{ service: data, reload } satisfies ServiceOutlet} />
      </Fragment>
    </>
  );
}
